import React from "react";

import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper, Checkbox } from "@mui/material";
import Box from "@mui/material/Box"
import {DataGrid} from "@mui/x-data-grid";

import {CxGrid} from "./CxGrid";
import {CxText, CxBoldText} from "./CxTypography";

// ----------------------------------------------------------------------------------------------------------------
// CxDataGrid
// ----------------------------------------------------------------------------------------------------------------

const paginationModel = { page: 0, pageSize: 25 };
const pageSizeOptions = [10, 25, 50, 100];

function addActionColumns({ columns, onViewAction, onEditAction, onCloneAction, onDocAction }) {
    // If no actions are provided, return columns without modification
    if (!onViewAction && !onEditAction && !onCloneAction) return columns;

    let actionColWidth = onCloneAction ? 110 : 80;

    return [
        {
            field: "actions",
            headerName: "Aktionen",
            width: actionColWidth,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: "flex", gap: 2 }}>
                    {onViewAction && (
                        <span
                            onClick={() => onViewAction(params)}
                            style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem" }}
                            title="Anzeige Details"
                        >
                            &#128065;  {/* &#128065; 👁️ */}
                        </span>
                    )}
                    {onEditAction && (
                        <span
                            onClick={() => onEditAction(params)}
                            style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem" }}
                            title="Bearbeiten"
                        >
                            &#128393; {/* ✏️ */}
                        </span>
                    )}
                    {onCloneAction && (
                        <span
                            onClick={() => onCloneAction(params)}
                            style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.3rem" }}
                            title="Kopieren"
                        >
                            &#11195;
                        </span>
                    )}
                </Box>
            ),
        },
        ...columns,
    ];
}


function addDocActionColumns(columns, onDownloadAction, onDeleteAction) {
    // If no actions are provided, return columns without modification
    if (!onDownloadAction && !onDeleteAction) return columns;

    let actionColWidth = 80;

    return [
        {
            field: "actions",
            headerName: "Aktionen",
            width: actionColWidth,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: "flex", gap: 2 }}>
                    {onDownloadAction && (
                        <span
                            onClick={() => onDownloadAction(params)}
                            style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem" }}
                            title="Datei herunterladen"
                        >
                            &#11015;    {/* Arrow Down */}
                        </span>
                    )}
                    {onDeleteAction && (
                        <span
                            onClick={() => onDeleteAction(params)}
                            style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem" }}
                            title="Datei löschen"
                        >
                            &#128465; {/* Delete Basket */}
                    </span>
                    )}
                </Box>
            ),
        },
        ...columns,
    ];
}


function addClearedColumn(columns, onToggleClearedAction) {
    if (!onToggleClearedAction) return columns;

    return [
        {
            field: "cleared",
            headerName: "Abgeräumt",
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <Checkbox
                    checked={!!params.row.cleared}
                    onChange={() =>
                        onToggleClearedAction(params.row, !params.row.cleared)
                    }
                    color="primary"
                />
            ),
        },
        ...columns,
    ];
}



export function CxDataGrid({
    rows, columns,
    onRowDoubleClick, onViewAction, onEditAction, onCloneAction, onDownloadAction, onDeleteAction,
    onToggleClearedAction, onSortModelChange,
    sx, noRecordsMessage = "Keine Einträge.", ...props }) {

    const actionColumns     = addActionColumns({columns, onViewAction, onEditAction, onCloneAction});
    const docActionsColumns = addDocActionColumns(actionColumns, onDownloadAction, onDeleteAction);
    const finalColumns      = addClearedColumn(docActionsColumns, onToggleClearedAction);

    const sxOptions = sx ?? { border: 0 };

    const isEmpty = rows === null || rows.length === 0 ? true : false;

    return (
        <>
            { isEmpty &&
                <CxText>{noRecordsMessage}</CxText>
            }
            { !isEmpty &&
                <DataGrid
                    rows={rows}
                    columns={finalColumns}
                    onRowDoubleClick={onRowDoubleClick}
                    onSortModelChange={onSortModelChange}
                    initialState={{ pagination: { paginationModel } }}
                    pageSizeOptions={pageSizeOptions}
                    sx={{
                        ...sxOptions,
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#E3F2FD', // MUI blue[50]
                        }
                    }}
                    {...props}
                />
            }
        </>
    );
}

// ----------------------------------------------------------------------------------------------------------------
// CxDataTable
// ----------------------------------------------------------------------------------------------------------------

export function CxDataTable({ rows, columns, onRowDoubleClick, onViewAction, onEditAction, onDocAction, sx, noRecordsMessage = "Keine Einträge.", ...props }) {
    const sxOptions = sx ?? { borderBottom: "1px solid rgba(224, 224, 224, 1)" };
    const isEmpty = !rows || rows.length === 0;


    // Add action column if actions exist
    const actionColumn = (onViewAction || onEditAction || onDocAction) ? [{
        field: "actions",
        headerName: "Aktionen",
        renderCell: (row) => (
            <Box sx={{ display: "flex", gap: 2 }}>
                {onViewAction && (
                    <span
                        onClick={() => onViewAction(row)}
                        style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem" }}
                        title={`Anzeige Details`}
                    >
                        &#128065; {/* Unicode for eye symbol 👁️ */}
                    </span>
                )}
                {onEditAction && (
                    <span
                        onClick={() => onEditAction(row)}
                        style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem" }}
                        title={`Bearbeiten`}
                    >
                        &#128393; {/* Unicode for pencil/edit ✏️ */}
                    </span>
                )}
                {onDocAction && row.doc_count === 0 && (
                    <span
                        onClick={() => onDocAction(row)}
                        style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem" }}
                        title={`Keine Dokumente`}
                    >
                        &#128460;
                    </span>
                )}
                {onDocAction && row.doc_count === 1 && (
                    <span
                        onClick={() => onDocAction(row)}
                        style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem", fontWeight: "bold" }}
                        title={`1 Dokument`}
                    >
                        &#128463;
                    </span>
                )}
                {onDocAction && row.doc_count > 1 && (
                    <span
                        onClick={() => onDocAction(row)}
                        style={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem", fontWeight: "bold" }}
                        title={`${row.doc_count} Dokumente`}
                    >
                        &#128464;
                    </span>
                )}
            </Box>
        ),
    }] : [];

    // Final columns including action column
    const finalColumns = [...actionColumn, ...columns];

    return (
        <>
            {isEmpty ? (
                <CxText>{noRecordsMessage}</CxText>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={sxOptions} {...props}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {finalColumns.map((col) => (
                                    <TableCell key={col.field} align={col.align || "left"}>
                                        {col.headerName}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, rowIndex) => (
                                <TableRow
                                    key={row.id || rowIndex}
                                    onDoubleClick={() => onRowDoubleClick?.(row)}
                                    sx={{
                                        backgroundColor: rowIndex % 2 === 1 ? "rgba(0, 0, 0, 0.04)" : "inherit",
                                        '&:hover': { backgroundColor: "#E3F2FD" }, // MUI blue[50]
                                        '&:last-child td, &:last-child th': { border: 0 },
                                    }}
                                >
                                    {finalColumns.map((col) => (
                                        <TableCell key={col.field} align={col.align || "left"}>
                                            {col.field === "actions" ? col.renderCell(row) : row[col.field]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
}

// ----------------------------------------------------------------------------------------------------------------
// CxDataPairs
// ----------------------------------------------------------------------------------------------------------------

/**
 * Anzeige von Schlüssel/Werte Paaren in einem Grid mit Grösse size. Die Items enthalten key, value und label.
 * Angezeigt werden label und value. Der key wird für die Komponente eines Paars verwendet.
 */
export function CxDataPairs({ items, size="lg"}) {

    if (!items || items.length === 0)
        return <CxText>Keine Einträge vorhanden.</CxText>

    return (
        <CxGrid container spacing={2}>
            {items.map((item) => (
                <CxGrid key={item.key} size={size}>
                    <CxGrid container spacing={0.5}>
                        <CxGrid size="xl" align="left">
                            <CxText>{item.label}</CxText>
                        </CxGrid>
                        <CxGrid size="xl" align="left">
                            <CxBoldText>{item.value}</CxBoldText>
                        </CxGrid>
                    </CxGrid>
                </CxGrid>
            ))}
        </CxGrid>
    );
};


